/**
 * Eine Leiste fuer alles, was die Sicht bestimmt: Reiter, Zeitraum, Person,
 * Hinweise, Betragssichtbarkeit, PDF, Einstellungen.
 */
import React from 'react';
import {
    AlertTriangle, BarChart3, Building2, Download, EyeOff, FileText,
    GanttChart, Gauge, Loader2, Settings, Users,
} from 'lucide-react';
import type { UserProfile } from '../../types';
import { useGesperrteKonten } from '../../hooks/useGesperrteKonten';
import { gesperrtZusatz } from '../../services/gesperrteKonten';
import { MO_FULL, type CockpitTab, type PeriodKind } from './cockpitUi';

const TABS: { key: CockpitTab; label: string; Icon: React.ElementType }[] = [
    { key: 'uebersicht', label: 'Übersicht', Icon: BarChart3 },
    { key: 'auslastung', label: 'Auslastung', Icon: Gauge },
    { key: 'team', label: 'Team', Icon: Users },
    { key: 'projekte', label: 'Projekte', Icon: Building2 },
    { key: 'gantt', label: 'Gantt', Icon: GanttChart },
    { key: 'angebote', label: 'Angebote', Icon: FileText },
];

const KINDS: { key: PeriodKind; label: string }[] = [
    { key: 'year', label: 'Jahr' },
    { key: 'quarter', label: 'Quartal' },
    { key: 'month', label: 'Monat' },
];

const CONTROL = 'h-8 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none';

export interface CockpitToolbarProps {
    tab: CockpitTab;
    onTab: (tab: CockpitTab) => void;
    year: number;
    years: number[];
    onYear: (year: number) => void;
    periodKind: PeriodKind;
    periodValue: number;
    onPeriod: (kind: PeriodKind, value: number) => void;
    employeeId: string;
    users: UserProfile[];
    onEmployee: (id: string) => void;
    warningCount: number;
    onWarnings: () => void;
    hideAmounts: boolean;
    onToggleAmounts: () => void;
    onExportPdf: () => void;
    exporting: boolean;
    onSettings: () => void;
}

export const CockpitToolbar: React.FC<CockpitToolbarProps> = ({
    tab, onTab, year, years, onYear, periodKind, periodValue, onPeriod,
    employeeId, users, onEmployee, warningCount, onWarnings,
    hideAmounts, onToggleAmounts, onExportPdf, exporting, onSettings,
}) => {
    // Filter über Vergangenes: Gesperrte (Nr. 148) bleiben wählbar, markiert.
    const gesperrteKonten = useGesperrteKonten();
    return (
    <div data-glass-view="cockpit" className="flex flex-col xl:flex-row justify-between items-stretch xl:items-center gap-3">
        {/* NUR die Reiter duerfen umbrechen. Sie tragen den Platzmangel,
            damit die Steuergruppe rechts in EINER Zeile bleibt. */}
        <div data-glass="umschalter" className="flex flex-wrap min-w-0 bg-slate-100 p-0.5 rounded-xl border border-slate-200">
            {TABS.map(({ key, label, Icon }) => (
                <button
                    key={key}
                    onClick={() => onTab(key)}
                    className={`px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 whitespace-nowrap transition-colors ${
                        tab === key ? 'bg-white text-slate-800' : 'text-slate-400 hover:text-slate-600'
                    }`}
                >
                    <Icon className="w-3.5 h-3.5" />{label}
                </button>
            ))}
        </div>

        {/* `flex-nowrap` + `shrink-0`: der Einstellungsknopf ist bei 1440 px in
            eine zweite Zeile gerutscht, sobald ein Mitarbeitername lang war. */}
        <div className="flex flex-nowrap shrink-0 items-center gap-2">
            <div data-glass="umschalter" className="flex bg-slate-100 p-0.5 rounded-xl border border-slate-200">
                {KINDS.map((k) => (
                    <button
                        key={k.key}
                        onClick={() => onPeriod(k.key, periodValue)}
                        className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold ${
                            periodKind === k.key ? 'bg-white text-blue-600' : 'text-slate-400 hover:text-slate-600'
                        }`}
                    >
                        {k.label}
                    </button>
                ))}
            </div>

            {periodKind !== 'year' && (
                <select
                    value={periodValue}
                    onChange={(e) => onPeriod(periodKind, Number(e.target.value))}
                    className={`${CONTROL} px-2 cursor-pointer`}
                    title={periodKind === 'quarter' ? 'Quartal' : 'Monat'}
                >
                    {periodKind === 'quarter'
                        ? [1, 2, 3, 4].map((q) => <option key={q} value={q}>{`Q${q}`}</option>)
                        : MO_FULL.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
            )}

            <select
                value={year}
                onChange={(e) => onYear(Number(e.target.value))}
                className={`${CONTROL} px-2 cursor-pointer font-semibold`}
                title="Geschäftsjahr"
            >
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>

            <select
                value={employeeId}
                onChange={(e) => onEmployee(e.target.value)}
                className={`${CONTROL} px-2 cursor-pointer w-[220px] max-w-[220px] truncate`}
                title="Verantwortliche Person"
            >
                <option value="all">Alle Mitarbeiter</option>
                {users.map((u) => (
                    <option key={u.id} value={u.id}>{(`${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email) + gesperrtZusatz(u.id, gesperrteKonten)}</option>
                ))}
                <option value="unassigned">Ohne Zuordnung</option>
            </select>

            {warningCount > 0 && (
                <button
                    onClick={onWarnings}
                    className="h-8 px-2.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-600 flex items-center gap-1 text-[11px] font-semibold"
                    title="Datenqualität"
                >
                    <AlertTriangle className="w-3.5 h-3.5" />{warningCount}
                </button>
            )}

            <button
                onClick={onToggleAmounts}
                className={`h-8 px-3 rounded-lg flex items-center gap-1.5 text-[11px] font-semibold border transition-colors ${
                    hideAmounts ? 'bg-blue-50 border-blue-200 text-blue-600' : 'bg-slate-50 border-slate-200 text-slate-400 hover:text-slate-600'
                }`}
                title={hideAmounts ? 'Beträge einblenden' : 'Beträge ausblenden'}
            >
                <EyeOff className="w-3.5 h-3.5" />{hideAmounts ? '€ ein' : '€ aus'}
            </button>

            <button
                onClick={onExportPdf}
                disabled={exporting}
                className="h-8 px-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-500 hover:text-blue-600 hover:border-blue-200 flex items-center gap-1.5 text-[11px] font-semibold disabled:opacity-50"
            >
                {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                {exporting ? '…' : 'PDF'}
            </button>

            <button
                onClick={onSettings}
                className="h-8 w-8 bg-slate-50 border border-slate-200 rounded-lg text-slate-400 hover:text-slate-600 flex items-center justify-center"
                title="Einstellungen"
            >
                <Settings className="w-3.5 h-3.5" />
            </button>
        </div>
    </div>
);
};
